import React, { useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Button,
  Row,
  Spinner,
} from "reactstrap";
import { useNavigate, Link } from "react-router-dom";
import { APIClient } from "../../helpers/api_helper";
import { setResidentAuth } from "../../helpers/residentauth";
import { toast } from "react-toastify";
import AnimatedBackground from "../../components/Animatedbackground";

const api = new APIClient();

const ResidentLogin: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email.trim()) return toast.warning("Email required!");
    if (!formData.password) return toast.warning("Password required!");

    setSubmitting(true);
    try {
      const res: any = await api.create("/api/resident/login", {
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
      });

      if (res?.token) {
        setResidentAuth(res.token, res.data);
        toast.success(`Welcome ${res.data.name}! 🎉`);
        setTimeout(() => navigate("/resident/dashboard"), 1000);
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Login failed!");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatedBackground>
      <Container>
        <Row className="justify-content-center">
          <Col md={8} lg={5} xl={4}>
            <Card
              className="border-0 shadow-lg"
              style={{ borderRadius: "20px" }}
            >
              <CardBody className="p-4 p-md-5">
                {/* Header */}
                <div className="text-center mb-4">
                  <div
                    style={{
                      width: "70px",
                      height: "70px",
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #764ba2, #764ba2)",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "16px",
                    }}
                  >
                    <i
                      className="ri-user-line"
                      style={{ fontSize: "32px", color: "#fff" }}
                    />
                  </div>
                  <h3 className="fw-bold mb-1" style={{ color: "#2d3748" }}>
                    Welcome Back!
                  </h3>
                  <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                    Login to Commercial Property Portal
                  </p>
                </div>

                <Form onSubmit={handleSubmit}>
                  <FormGroup className="mb-3">
                    <Label
                      className="fw-semibold mb-1"
                      style={{ fontSize: "13px" }}
                    >
                      <i className="ri-mail-line me-1 text-primary" />
                      Email
                    </Label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      style={{
                        height: "44px",
                        borderRadius: "10px",
                        border: "1.5px solid #e2e8f0",
                        fontSize: "14px",
                      }}
                    />
                  </FormGroup>

                  <FormGroup className="mb-3">
                    <Label
                      className="fw-semibold mb-1"
                      style={{ fontSize: "13px" }}
                    >
                      <i className="ri-lock-line me-1 text-primary" />
                      Password
                    </Label>
                    <div style={{ position: "relative" }}>
                      <Input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Enter password"
                        style={{
                          height: "44px",
                          borderRadius: "10px",
                          border: "1.5px solid #e2e8f0",
                          fontSize: "14px",
                          paddingRight: "40px",
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          color: "#6b7280",
                        }}
                      >
                        <i
                          className={`ri-${showPassword ? "eye-off" : "eye"}-line`}
                        />
                      </button>
                    </div>
                  </FormGroup>

                  <Button
                    type="submit"
                    color="primary"
                    block
                    disabled={submitting}
                    className="mt-4"
                    style={{
                      height: "48px",
                      fontSize: "15px",
                      fontWeight: 600,
                      borderRadius: "10px",
                      background: "radial-gradient(circle at 50% -20%, #1e293b, #0f172a, #070a12)",
                      border: "none",
                    }}
                  >
                    {submitting ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Logging in...
                      </>
                    ) : (
                      <>
                        <i className="ri-login-circle-line me-2" />
                        Login
                      </>
                    )}
                  </Button>
                  <div className="text-center my-3">
                    <hr />
                    <small className="text-muted">— OR —</small>
                  </div>

                  <Button
                    color="warning"
                    block
                    onClick={() => navigate("/quick-pay-bill")}
                    style={{ fontWeight: 600 }}
                  >
                    <i className="ri-flashlight-line me-1" />
                    Pay Without Login
                  </Button>

                  <div className="text-center mt-3">
                    <span className="text-muted" style={{ fontSize: "14px" }}>
                      Forgot your password ?{" "}
                      <Link
                        to="/resident/forgetpassword"
                        style={{
                          color: "#764ba2",
                          fontWeight: 600,
                          textDecoration: "none",
                        }}
                      >
                        reset password
                      </Link>
                    </span>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
   </AnimatedBackground>
  );
};

export default ResidentLogin;
